import React from 'react';
import FallbackImage from '../../components/FallbackImage/FallbackImage';

const ApplicationsPage = () => {
  const applicationCategories = [
    {
      id: "beverages",
      title: "Beverage Applications",
      description: "From refreshing juices to premium cocktails, our fruit pulps create exceptional beverages for every occasion.",
      image: "beverage-applications.webp",
      products: [
        "Fruit Juices & Nectars",
        "Smoothies & Shakes", 
        "Energy Drinks",
        "Cocktails & Mocktails",
        "Fruit Concentrates",
        "Fruit Syrups",
        "Fruit Sodas",
        "Fruit Teas",
        "Fruit Water"
      ],
      fruitPulps: ["Mango", "Guava", "Pineapple", "Passion Fruit", "Coconut", "Chikoo", "Mixed Fruit", "Avocado"]
    },
    {
      id: "desserts",
      title: "Dessert Applications", 
      description: "Create indulgent desserts and frozen treats that capture the authentic taste of premium fruits.",
      image: "dessert-applications.webp",
      products: [
        "Ice Creams & Gelatos",
        "Frozen Yogurts & Sorbets",
        "Cakes & Pastries",
        "Mousses & Puddings",
        "Fruit Tarts & Pies",
        "Fruit Cheesecakes",
        "Fruit Parfaits",
        "Fruit Soufflés",
        "Fruit Trifles"
      ],
      fruitPulps: ["Mango", "Pineapple", "Passion Fruit", "Jackfruit", "Tender Coconut", "Chikoo", "Tender Coconut Date"]
    },
    {
      id: "jams",
      title: "Jam & Preserve Applications",
      description: "Natural sweetness and authentic flavors for premium jams, jellies, and preserves.",
      image: "jam-applications.webp", 
      products: [
        "Fruit Jams",
        "Fruit Jellies",
        "Fruit Preserves",
        "Fruit Spreads",
        "Fruit Compotes",
        "Fruit Marmalades",
        "Fruit Chutneys",
        "Fruit Conserves",
        "Fruit Butters"
      ],
      fruitPulps: ["Guava", "Mango", "Pineapple", "Passion Fruit", "Jackfruit", "Coconut", "Chikoo"]
    },
    {
      id: "food-service",
      title: "Food Service Applications",
      description: "Premium ingredients for restaurants, cafes, and catering services worldwide.",
      image: "food-service-applications.webp",
      products: [
        "Restaurant Sauces",
        "Café Beverages", 
        "Catering Products",
        "Hotel Amenities",
        "Institutional Food",
        "Quick Service Restaurants",
        "Fine Dining",
        "Buffet Services",
        "Event Catering"
      ],
      fruitPulps: ["Mango", "Guava", "Pineapple", "Passion Fruit", "Jackfruit", "Chikoo", "Mixed Fruit"]
    },
    {
      id: "culinary",
      title: "Culinary Applications",
      description: "Gourmet ingredients for innovative culinary creations and fusion cuisine.",
      image: "culinary-applications.webp",
      products: [
        "Gourmet Sauces",
        "Fusion Cuisine",
        "Fine Dining",
        "Artisanal Products", 
        "Specialty Foods",
        "Chef's Creations",
        "Molecular Gastronomy",
        "Contemporary Cuisine",
        "International Dishes"
      ],
      fruitPulps: ["Jackfruit", "Coconut", "Avocado", "Mango", "Passion Fruit"]
    },
    {
      id: "health",
      title: "Health & Nutrition Applications",
      description: "Nutrient-rich ingredients for health-conscious products and wellness applications.",
      image: "health-applications.webp",
      products: [
        "Health Drinks",
        "Nutritional Supplements",
        "Protein Shakes",
        "Wellness Products",
        "Functional Foods",
        "Dietary Supplements",
        "Superfood Blends",
        "Detox Drinks",
        "Energy Bars"
      ],
      fruitPulps: ["Avocado", "Tender Coconut Date", "Mango", "Guava", "Passion Fruit", "Tender Coconut"]
    },
    {
      id: "baby-food",
      title: "Baby Food Applications",
      description: "Pure, natural ingredients for safe and nutritious baby and toddler foods.",
      image: "Baby-Food-Applications.webp",
      products: [
        "Baby Purees",
        "Toddler Foods",
        "Infant Formulas",
        "Children's Snacks",
        "Kids' Beverages",
        "Educational Foods",
        "Organic Baby Food",
        "Stage-based Foods",
        "Allergen-free Options"
      ],
      fruitPulps: ["Mango", "Guava", "Pineapple", "Coconut", "Chikoo", "Mixed Fruit"]
    },
    {
      id: "industrial",
      title: "Industrial Applications",
      description: "Bulk ingredients for large-scale food processing and manufacturing operations.",
      image: "Industrial-Applications.webp",
      products: [
        "Food Processing",
        "Bakery Products",
        "Confectionery",
        "Dairy Products",
        "Snack Foods",
        "Ready-to-Eat Foods",
        "Frozen Foods",
        "Canned Products",
        "Packaged Goods"
      ],
      fruitPulps: ["All Fruit Pulps"]
    }
  ];

  return (
    <div className="applications-page min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#0B1E2B] to-[#1a3344] text-white pt-28 md:pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Applications & Products
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
              Discover the endless possibilities with our premium fruit pulps. From beverages to desserts, 
              health foods to industrial applications - we provide the perfect ingredients for your products.
            </p>
          </div>
        </div>
      </section>

      {/* Applications Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {applicationCategories.map((category) => (
              <div key={category.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                {/* Image */}
                <div className="relative aspect-square overflow-hidden bg-[#F3F4F6]">
                  <FallbackImage
                    src={`/media/${category.image}`}
                    alt={category.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    placeholderText={category.title}
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-[#0B1E2B] mb-3">
                    {category.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {category.description}
                  </p>

                  {/* Products List */}
                  <div className="mb-4">
                    <h4 className="text-lg font-semibold text-[#0B1E2B] mb-2">Products:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {category.products.slice(0, 5).map((product, index) => (
                        <li key={index} className="flex items-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#FF6B35] mr-2 flex-shrink-0"></div>
                          {product}
                        </li>
                      ))}
                      {category.products.length > 5 && (
                        <li className="text-[#FF6B35] font-medium">
                          +{category.products.length - 5} more products
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Fruit Pulps */}
                  <div>
                    <h4 className="text-lg font-semibold text-[#0B1E2B] mb-2">Suitable Fruit Pulps:</h4>
                    <div className="flex flex-wrap gap-2">
                      {category.fruitPulps.map((pulp, index) => (
                        <span key={index} className="bg-[#F3F4F6] text-[#0B1E2B] px-3 py-1 rounded-full text-sm font-medium">
                          {pulp}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-gradient-to-r from-[#FF6B35] to-[#e85a2b] py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Create Amazing Products?
          </h2>
          <p className="text-xl text-white opacity-90 mb-8 max-w-3xl mx-auto">
            Partner with Zenvia Foods to access premium fruit pulps for your applications. 
            From small-scale artisanal products to large industrial operations, we have the perfect solution for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact-us" 
              className="inline-block bg-white text-[#FF6B35] font-semibold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-300"
            >
              Get Custom Quote
            </a>
            <a 
              href="/products" 
              className="inline-block border-2 border-white text-white font-semibold py-3 px-8 rounded-lg hover:bg-white hover:text-[#FF6B35] transition-colors duration-300"
            >
              View Our Products
            </a>
          </div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1E2B] mb-6">
              Why Choose Zenvia Foods Fruit Pulp for Your Applications?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our fruit pulps are processed to meet the highest standards for various applications, 
              ensuring consistent quality and authentic flavors in every product you create.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-[#F3F4F6] rounded-xl">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="text-xl font-bold text-[#0B1E2B] mb-3">Premium Quality</h3>
              <p className="text-gray-600">FSSAI, ISO, and HACCP certified processing ensures the highest quality standards.</p>
            </div>
            <div className="text-center p-6 bg-[#F3F4F6] rounded-xl">
              <div className="text-4xl mb-4">🌱</div>
              <h3 className="text-xl font-bold text-[#0B1E2B] mb-3">100% Natural</h3>
              <p className="text-gray-600">No artificial preservatives, colors, or additives - just pure fruit goodness.</p>
            </div>
            <div className="text-center p-6 bg-[#F3F4F6] rounded-xl">
              <div className="text-4xl mb-4">📦</div>
              <h3 className="text-xl font-bold text-[#0B1E2B] mb-3">Flexible Packaging</h3>
              <p className="text-gray-600">Available in bulk aseptic packs, drums, and consumer-ready packaging.</p>
            </div>
            <div className="text-center p-6 bg-[#F3F4F6] rounded-xl">
              <div className="text-4xl mb-4">🚚</div>
              <h3 className="text-xl font-bold text-[#0B1E2B] mb-3">Reliable Supply</h3>
              <p className="text-gray-600">Consistent production capacity and supply chain for your business needs.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApplicationsPage;
